import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Clock, Info, AlertTriangle } from "lucide-react";
import Panel from "../../components/ui/Panel/Panel.jsx";
import Button from "../../components/ui/Button/Button.jsx";
import Tag from "../../components/ui/Tag/Tag.jsx";
import SeoHead from "../../seo/SeoHead.jsx";
import ShareRow from "../../components/share/ShareRow.jsx";
import RichText from "../../components/marketing/RichText.jsx";
import MarketingArt from "../../components/marketing/MarketingArt.jsx";
import { blogImage } from "../../data/blogImages.js";
import { articleSchema, breadcrumbSchema, faqSchema } from "../../seo/schema.js";
import { plainText } from "../../seo/richText.js";
import { SITE, PRICE_FROM } from "../../seo/siteMeta.js";
import { getPost, relatedPosts, CATEGORIES, CONTENT_VERIFIED_FOR } from "../../data/blogPosts.js";
import { currentPeriod } from "../../data/taxPeriods.js";
import { getPostEs, relatedPostsEs, esSlugFor, CATEGORIES_ES } from "../../data/blogPostsEs.js";

/**
 * Renders ANY post from src/data/blogPosts.js. Adding an article is a data
 * change - no new component, no new route, no build config. See the header
 * comment in blogPosts.js for why that matters.
 *
 * All prose runs through <RichText>, which turns [label](/path) in the source
 * text into a real link. Headings deliberately do not: a link inside an <h2> is
 * a heading that says two things at once, and the h2 text is also the anchor id
 * and the table-of-contents label.
 */

// Turns a heading into a stable anchor id so the table of contents can link to
// it. Deterministic, so ids don't shift between renders or builds.
const slugifyHeading = (text) =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");

// The site-wide closing call to action, used by any post that doesn't define
// its own `cta`. Posts written for a filing audience (fleets, preparers) carry a
// `cta` so the button names the job the reader came to do.
// Interface text by page language. Article text comes from the post data.
const UI = {
  en: {
    back: "All guides",
    backTo: "/blog",
    minRead: "min read",
    checked: (label) => `Checked for the ${label} tax period`,
    published: "Published",
    updated: "Updated",
    dateLocale: "en-US",
    onThisPage: "On this page",
    faq: "Frequently asked questions",
    related: "Related guides",
    switchLabel: "Leer en español",
    disclaimer:
      "This guide is general information drawn from the IRS Instructions for Form 2290 (Rev. July 2026), not tax advice for your situation. For the full instructions, see IRS.gov/Form2290.",
    sources: "Sources consulted:",
    home: "Home",
    hub: { name: "Blog", path: "/blog" },
  },
  es: {
    back: "Todas las guías en español",
    backTo: "/es",
    minRead: "min de lectura",
    checked: (label) => `Revisado para el período tributario ${label}`,
    published: "Publicado",
    updated: "Actualizado",
    dateLocale: "es-US",
    onThisPage: "En esta página",
    faq: "Preguntas frecuentes",
    related: "Guías relacionadas",
    switchLabel: "Read in English",
    disclaimer:
      "Esta guía es información general basada en las Instrucciones para el Formulario 2290(SP) (Rev. julio de 2026) del IRS; no es asesoría tributaria para su caso. Las instrucciones completas están en IRS.gov.",
    sources: "Fuentes consultadas:",
    home: "Inicio",
    hub: { name: "Guías en español", path: "/es" },
  },
};

const DEFAULT_CTA = {
  heading: `File Form 2290 from $${PRICE_FROM}`,
  text: "Amendments, VIN corrections, and credits all supported. Stamped Schedule 1 in seconds.",
  label: "Start filing",
  to: "/wizard/1",
};

// A call to action panel. Used three ways: the closing panel on every post, a
// `{ type: "cta" }` body block placed mid-article, and (compact) right under the
// intro when the post has a `cta`, so a reader who arrived ready to file never
// has to scroll 1,500 words to find the button.
const CtaPanel = ({ cta, compact = false }) => (
  <Panel
    padded
    className={`!bg-[var(--accent-soft)] flex flex-wrap items-center justify-between ${
      compact ? "gap-4 mt-6 !py-[18px]" : "gap-6 mt-12 !p-[30px]"
    }`}
  >
    <div>
      <b className={`font-disp ${compact ? "text-[16px]" : "text-[19px]"}`}>{cta.heading}</b>
      {!compact && cta.text && (
        <p className="text-[var(--muted)] text-sm mt-1 max-w-[46ch]">
          <RichText text={cta.text} />
        </p>
      )}
    </div>
    <Link to={cta.to}>
      <Button>
        {cta.label} <ArrowRight size={16} />
      </Button>
    </Link>
  </Panel>
);

const Block = ({ block }) => {
  switch (block.type) {
    case "cta":
      return <CtaPanel cta={block} />;

    case "h2":
      return (
        <h2
          id={slugifyHeading(block.text)}
          className="font-disp text-[25px] leading-[1.2] mt-10 mb-3 scroll-mt-24"
        >
          {block.text}
        </h2>
      );

    case "h3":
      return <h3 className="font-disp text-[19px] mt-7 mb-2">{block.text}</h3>;

    case "p":
      return (
        <p className="mt-3.5 text-[15.5px] leading-[1.7] max-w-[70ch]">
          <RichText text={block.text} />
        </p>
      );

    case "ul":
      return (
        <ul className="mt-3.5 pl-5 list-disc grid gap-2 max-w-[70ch] text-[15.5px] leading-[1.65]">
          {block.items.map((item) => (
            <li key={item}>
              <RichText text={item} />
            </li>
          ))}
        </ul>
      );

    case "ol":
      return (
        <ol className="mt-3.5 pl-5 list-decimal grid gap-2 max-w-[70ch] text-[15.5px] leading-[1.65]">
          {block.items.map((item) => (
            <li key={item}>
              <RichText text={item} />
            </li>
          ))}
        </ol>
      );

    case "table":
      return (
        // Wide tables scroll inside their own container so the page body never
        // scrolls horizontally on a phone.
        <Panel className="overflow-hidden mt-6">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[480px] text-sm">
              <thead>
                <tr>
                  {block.headers.map((h) => (
                    <th
                      key={h}
                      className="text-left font-disp font-semibold p-[11px_14px] border-b border-[var(--line)] bg-[var(--bg-soft)] whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row) => (
                  <tr key={row.join("|")}>
                    {row.map((cell, i) => (
                      <td
                        key={i}
                        className="p-[10px_14px] border-b border-[var(--line)] text-[var(--muted)]"
                      >
                        {/* Cells take links too. The "which filing do I need"
                            decision tables are the single best internal-linking
                            surface in the whole library — every row names a
                            filing type that has its own guide. */}
                        <RichText text={cell} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      );

    case "callout": {
      const isWarn = block.tone === "warn";
      const Icon = isWarn ? AlertTriangle : Info;
      return (
        <Panel
          padded
          className={`mt-6 flex gap-3 items-start ${
            isWarn ? "!bg-[#fef3c7] !border-[#f59e0b]" : "!bg-[var(--accent-soft)]"
          }`}
        >
          <Icon size={18} className="flex-none mt-0.5 text-[var(--accent)]" />
          <p className="text-[15px] leading-[1.6] m-0">
            <RichText text={block.text} />
          </p>
        </Panel>
      );
    }

    default:
      return null;
  }
};

const DEFAULT_CTA_ES = {
  heading: `Presente el Formulario 2290 desde $${PRICE_FROM}`,
  text: "Cree su cuenta gratis y presente en línea. La aplicación para presentar está en inglés.",
  label: "Crear su cuenta gratis",
  to: "/login?tab=up",
};

// `lang` comes from the route: /blog/:slug is English, /es/:slug Spanish.
const BlogPost = ({ lang = "en" }) => {
  const { slug } = useParams();
  const es = lang === "es";
  const t = UI[lang];
  const post = es ? getPostEs(slug) : getPost(slug);

  // An unknown slug must not render a soft-404. Redirect to the index rather
  // than showing an empty article shell.
  if (!post) return <Navigate to={t.backTo} replace />;

  const path = es ? `/es/${post.slug}` : `/blog/${post.slug}`;
  // The other-language version of this page, if there is one.
  const esSlug = es ? undefined : esSlugFor(post.slug);
  const otherPath = es ? `/blog/${post.translationOf}` : esSlug ? `/es/${esSlug}` : undefined;
  const related = es ? relatedPostsEs(slug) : relatedPosts(slug);
  const relatedBase = es ? "/es" : "/blog";
  const headings = post.body.filter((b) => b.type === "h2");
  // Lead images are English artwork with English alt text - Spanish guides go without.
  const hero = es ? undefined : blogImage(slug);
  // Absolute — og:image and Article.image are both ignored when relative.
  // Undefined when the post has no art, which lets SeoHead fall back to the
  // site-wide OG image rather than emitting a link to nothing.
  const heroUrl = hero ? `${SITE.url}/images/${hero.file}` : undefined;

  return (
    <div className="max-w-[820px] mx-auto px-[22px] py-[46px] pb-[70px]">
      <SeoHead
        title={post.metaTitle}
        description={post.metaDescription}
        path={path}
        keywords={post.keywords}
        image={heroUrl}
        type="article"
        publishedAt={post.publishedAt}
        updatedAt={post.updatedAt}
        lang={lang}
        alternates={otherPath ? [{ hreflang: es ? "en" : "es", path: otherPath }] : []}
        schemas={[
          articleSchema(post, heroUrl, { path, lang }),
          breadcrumbSchema([
            { name: t.home, path: "/" },
            t.hub,
            { name: post.title, path },
          ]),
          // Only emitted when FAQs actually render on the page - marking up
          // content that isn't visible is a structured-data violation.
          ...(post.faqs?.length ? [faqSchema(post.faqs)] : []),
        ]}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to={t.backTo}
          className="text-[var(--muted)] text-sm no-underline inline-flex items-center gap-1.5 hover:text-[var(--accent)]"
        >
          <ArrowLeft size={15} /> {t.back}
        </Link>
        {otherPath && (
          <Link to={otherPath} hrefLang={es ? "en" : "es"} className="text-sm font-semibold text-[var(--accent)] no-underline">
            {t.switchLabel}
          </Link>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2.5 mt-5">
        <Tag tone="default">{(es ? CATEGORIES_ES : CATEGORIES)[post.category]}</Tag>
        {currentPeriod().taxYear === CONTENT_VERIFIED_FOR && (
          <Tag tone="default">{t.checked(currentPeriod().label)}</Tag>
        )}
        <span className="text-[var(--muted)] text-xs inline-flex items-center gap-1">
          <Clock size={13} /> {post.readingMinutes} {t.minRead}
        </span>
      </div>

      <h1 className="text-[34px] leading-[1.13] mt-3 font-disp">{post.title}</h1>

      {/* The excerpt is plain text by convention — it is also the blog-index
          card copy and the Article schema description, neither of which can
          render a link. plainText() means a link pasted here degrades to its
          label rather than showing raw "[…](…)" on the page. */}
      <p className="text-[var(--muted)] mt-3 text-[16px] leading-[1.6] max-w-[68ch]">
        {plainText(post.excerpt)}
      </p>

      {post.cta && <CtaPanel cta={post.cta} compact />}

      {/* The lead image. Article pages carried no image at all until 2026-08-14
          — only the blog-index cards did — which meant the page that actually
          ranks had nothing for image search and nothing to break up 1,200 words
          of tax copy.

          `eager` because this is the LCP element on an article: lazy-loading
          something above the fold delays the very paint it is measured by.

          Rendered only when the post HAS art. MarketingArt returns null on a
          404, so pointing this at a missing file would leave the sized <figure>
          behind as an empty grey box — the three newest articles are absent
          from BLOG_IMAGES for exactly that reason. */}
      {hero && (
        <figure className="mt-7 rounded-[16px] overflow-hidden border border-[var(--line)] bg-[var(--panel-2)]">
          <MarketingArt
            src={hero.file}
            alt={hero.alt}
            eager
            className="w-full h-auto block"
          />
        </figure>
      )}

      {/* Dates only. The byline was removed 2026-08-14 by owner decision: the
          only author it could honestly name was the company itself, and a
          named human reviewer is a commitment (a real person, really reviewing)
          that the project has not made. Neither field is required by Article
          structured data, so this is a smaller page, not a broken one. */}
      <div className="text-[var(--muted)] text-xs mt-5 pb-5 border-b border-[var(--line)]">
        {t.published}{" "}
        <time dateTime={post.publishedAt}>
          {new Date(`${post.publishedAt}T00:00:00`).toLocaleDateString(t.dateLocale, {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </time>
        {post.updatedAt && post.updatedAt !== post.publishedAt && (
          <>
            {" "}· {t.updated}{" "}
            <time dateTime={post.updatedAt}>
              {new Date(`${post.updatedAt}T00:00:00`).toLocaleDateString(t.dateLocale, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </time>
          </>
        )}
      </div>

      {headings.length > 2 && (
        <Panel padded className="mt-7 !bg-[var(--bg-soft)]">
          <b className="font-disp text-sm uppercase tracking-[.1em] text-[var(--muted)]">{t.onThisPage}</b>
          <ul className="mt-2.5 grid gap-1.5 pl-4 list-disc text-sm">
            {headings.map((h) => (
              <li key={h.text}>
                <a href={`#${slugifyHeading(h.text)}`} className="text-[var(--accent)] no-underline">
                  {h.text}
                </a>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <article className="text-[var(--ink)]">
        {post.body.map((block, i) => (
          <Block key={i} block={block} />
        ))}
      </article>

      <ShareRow path={`${path}/`} title={post.title} lang={lang} />

      {post.faqs?.length > 0 && (
        <section className="mt-12">
          <h2 className="font-disp text-[25px] leading-[1.2] mb-4">{t.faq}</h2>
          <div className="grid gap-3">
            {post.faqs.map((f) => (
              <Panel key={f.q} padded>
                <b className="font-disp text-[16px]">{f.q}</b>
                <p className="text-[var(--muted)] text-sm mt-1.5 leading-[1.65] max-w-[68ch]">
                  {/* Answers may carry links; faqSchema() strips them back to
                      the visible label so the JSON-LD never ships raw markup. */}
                  <RichText text={f.a} />
                </p>
              </Panel>
            ))}
          </div>
        </section>
      )}

      <CtaPanel cta={post.cta || (es ? DEFAULT_CTA_ES : DEFAULT_CTA)} />

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="font-disp text-[21px] mb-3.5">{t.related}</h2>
          <div className="grid gap-3">
            {related.map((r) => (
              <Panel key={r.slug} padded className="hover:border-[var(--accent)] transition-colors">
                <Link to={`${relatedBase}/${r.slug}`} className="no-underline text-inherit">
                  <b className="font-disp text-[16px]">{r.title}</b>
                  <p className="text-[var(--muted)] text-sm mt-1.5 max-w-[64ch]">{r.excerpt}</p>
                </Link>
              </Panel>
            ))}
          </div>
        </section>
      )}

      <p className="text-[var(--muted)] text-xs mt-11 pt-5 border-t border-[var(--line)] max-w-[70ch]">
        {t.disclaimer}
        {post.sources && <> {t.sources} {post.sources}.</>}
      </p>
    </div>
  );
};

export default BlogPost;
